import type { GenerateResult, Module } from "@/types/artwork/generate";
import { computed, ref, type Ref } from "vue";
import { request } from "@/axios";
import { startGenerate } from "@/assets/artwork";
import { resolveStoreModuleId } from "@/assets/artwork/moduleEnum";
import { jsonParse } from "@/utils";
import type { ModuleId } from "@xmiles/holopix-types";

export function queryGeneratingVersions(data: { artworkId: number }) {
  return request<
    {
      param: string;
      clientId: string;
      ctime: string;
    }[]
  >({
    api: "/api/task/waitClientIds",
    method: "post",
    data,
    notToast: false,
  });
}

interface GeneratingVersionState {
  module: Module;
  clientId: string;
  params: any;
  socket?: WebSocket;
  cancel?: Function;
  result: Partial<GenerateResult>;
}

// 当前正在生成的作品列表
const generatingVersionList: Ref<GeneratingVersionState[]> = ref([]);

// 存储当前活动的clientId设置回调
let currentClientIdSetter: ((clientId: string, module?: Module) => void) | null = null;

export function useGeneratingVersions() {
  function getVersionIndex(clientId: string) {
    return generatingVersionList.value.findIndex(
      (item) => item.clientId === clientId
    );
  }

  function has(clientId: string) {
    return getVersionIndex(clientId) > -1;
  }

  function get(clientId: string) {
    const index = getVersionIndex(clientId);
    return generatingVersionList.value[index];
  }

  function set(data: GeneratingVersionState) {
    const index = getVersionIndex(data.clientId);

    if (index > -1) {
      generatingVersionList.value[index] = data;
    } else {
      generatingVersionList.value.unshift(data);
    }
  }

  function updateResult(
    clientId: string,
    data: Partial<GeneratingVersionState["result"]>
  ) {
    const index = getVersionIndex(clientId);

    if (index > -1) {
      generatingVersionList.value[index].result = {
        ...generatingVersionList.value[index].result,
        ...data,
      };
    }
  }

  function updateParams(clientId: string, data: any) {
    const index = getVersionIndex(clientId);
    generatingVersionList.value[index].params = {
      ...generatingVersionList.value[index].params,
      ...data,
    };
  }

  function isModuleLatest(client: string, { empty = false } = {}) {
    const index = getVersionIndex(client);

    if (index === -1) {
      return !!empty;
    }

    const module = resolveStoreModuleId(generatingVersionList.value[index].module as number);

    return !generatingVersionList.value.some((item, itemIndex) => {
      return resolveStoreModuleId(item.module as number) === module && itemIndex < index;
    });
  }

  function getModuleLatest(module: Module) {
    const storeModule = resolveStoreModuleId(module as number);
    const index = generatingVersionList.value.findIndex(
      (item) => resolveStoreModuleId(item.module as number) === storeModule
    );
    return generatingVersionList.value[index];
  }

  function deleteVersion(clientId: string) {
    const index = getVersionIndex(clientId);

    if (index > -1) {
      generatingVersionList.value[index]?.socket?.close();
      generatingVersionList.value[index].cancel &&
        generatingVersionList.value[index].cancel();
      generatingVersionList.value.splice(index, 1);
    }
  }

  /**
   * 删除指定模块的所有生成任务
   * 用于页面卸载时清理该模块的生成状态，防止泄漏到其他页面
   */
  function deleteVersionsByModule(module: Module) {
    const storeModule = resolveStoreModuleId(module as number);
    const indices: number[] = [];
    generatingVersionList.value.forEach((item, index) => {
      if (resolveStoreModuleId(item.module as number) === storeModule) {
        indices.push(index);
      }
    });
    // 从后往前删除，避免索引变化
    indices.reverse().forEach((index) => {
      generatingVersionList.value[index]?.socket?.close();
      generatingVersionList.value[index].cancel &&
        generatingVersionList.value[index].cancel();
      generatingVersionList.value.splice(index, 1);
    });
  }

  function clear() {
    generatingVersionList.value.forEach((item) => {
      item.socket?.close();
      item.cancel && item.cancel();
    });
    generatingVersionList.value = [];
  }

  // 注册当前活动的clientId设置回调
  function registerClientIdSetter(setter: (clientId: string, module?: Module) => void) {
    currentClientIdSetter = setter;
  }

  // 注销clientId设置回调
  function unregisterClientIdSetter() {
    currentClientIdSetter = null;
  }

  async function init(artworkId: number) {
    let { data } = await queryGeneratingVersions({
      artworkId,
    });

    data = data.reverse();

    data.forEach((item) => {
      const params = jsonParse(item.param);
      
      // 重新连接任务时，如果有注册的clientId设置器，通知设置器有新的clientId
      if (currentClientIdSetter && params.module && item.clientId) {
        // 传递模块信息和clientId，让设置器决定是否需要设置
        currentClientIdSetter(item.clientId, resolveStoreModuleId(params.module) as Module);
      }
      startGenerate(resolveStoreModuleId(params.module) as unknown as ModuleId, {
        ...params,
        clientId: item.clientId,
        sendType: "doReConnect",
        startTime: new Date(item.ctime).getTime(),
      });
    });
  }


  return {
    has,
    get,
    set,
    updateResult,
    updateParams,
    isModuleLatest,
    getModuleLatest,
    del: deleteVersion,
    deleteByModule: deleteVersionsByModule,
    clear,
    init,
    registerClientIdSetter,
    unregisterClientIdSetter,
    generatingVersionList,
  };
}
